import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrderView } from "@townsy/contracts";
import { WORLD_MANIFEST, getPlot } from "@townsy/world";
import { isApiError } from "../../api/client";
import { fetchNotifications, fetchWalletHistory, postNotificationRead } from "../../api/endpoints";
import { invalidateAdvertising } from "../../api/advertisingQueries";
import { storeOffersKeys } from "../../api/storeOffers";
import { storeSetupKeys } from "../../api/storeSetup";
import { queryKeys, useBootstrapQuery, useOrderQuery, useWalletsQuery } from "../../api/queries";
import { useTownSession } from "../../app/TownSessionContext";
import { fulfilledToastActions, fulfilledToastText, refreshAfterOrder, shouldAnnounceFulfilled } from "../../commerce/afterOrder";
import { SeenNotices, noticeFromCreditHistory, noticeFromNotification, type CommerceNotice } from "../../commerce/notifications";
import { isSettled, orderPollDelayMs } from "../../commerce/orderStatus";
import { useTownStore } from "../../state/store";
import { CheckoutDialog } from "./CheckoutDialog";

/** Orders already announced in this tab (one toast per fulfilled order, whatever reported it). */
const announced = new Set<string>();
/** Last status seen per order, so caches are refreshed once per change. */
const lastStatus = new Map<string, string>();

/**
 * React to a fresh order view from the server: refresh what it changed (once per status change)
 * and announce a completed purchase once. Shared by the dialog, the background watcher and the
 * order page.
 */
export function useOrderEffects(): (order: OrderView) => void {
  const qc = useQueryClient();
  const session = useTownSession();
  const navigate = useNavigate();
  const pushToast = useTownStore((s) => s.pushToast);
  const openThemePicker = useTownStore((s) => s.openThemePicker);
  const openStoreSetup = useTownStore((s) => s.openStoreSetup);
  // From the bootstrap query, not the town store: the store's copy is set only inside the 3D town, so on the
  // plain pages (/directory, /orders/:id) the "already announced" mark was never saved and the same purchase
  // was announced again by the notifier after a reload.
  const userId = useBootstrapQuery().data?.session.userId ?? null;
  const ref = useRef({ qc, session, navigate, pushToast, openThemePicker, openStoreSetup, userId });
  ref.current = { qc, session, navigate, pushToast, openThemePicker, openStoreSetup, userId };
  return useCallback((order: OrderView) => {
    const { qc, session, navigate, pushToast, openThemePicker, openStoreSetup, userId } = ref.current;
    qc.setQueryData(queryKeys.order(order.orderId), order);
    if (lastStatus.get(order.orderId) === order.status) return;
    const first = !lastStatus.has(order.orderId);
    lastStatus.set(order.orderId, order.status);
    // Holds and credit change at every step (reserved, released, consumed): balances are re-read.
    refreshAfterOrder(order, qc, session);
    if (order.status === "fulfilled" && !announced.has(order.orderId)) {
      announced.add(order.orderId);
      // Announced once per account in this browser: a page that loads later (a reload, the receipt opened in
      // a new tab within minutes of the purchase) must not announce it again; an old receipt never is.
      const seen = userId ? new SeenNotices(userId) : null;
      const alreadyAnnounced = seen?.has(`o:${order.orderId}`) ?? false;
      seen?.markSeen([`o:${order.orderId}`]);
      if (shouldAnnounceFulfilled({ firstSeen: first, fulfilledAt: order.fulfilledAt, now: Date.now(), alreadyAnnounced })) {
        pushToast(
          fulfilledToastText(order),
          "success",
          fulfilledToastActions(order, {
            viewReceipt: (id) => navigate(`/orders/${id}`),
            chooseInterior: openThemePicker,
            setUpStore: (id) => openStoreSetup(id, "purchase"),
          }),
        );
      }
    }
  }, []);
}

/**
 * The one checkout dialog of the page (town or 2D pages), the background watcher for orders that
 * are still settling after their dialog closed, and the commerce notifier. Mounted once in the town
 * layout and once in the plain page shell.
 */
export function CheckoutHost() {
  const request = useTownStore((s) => s.checkout);
  const close = useTownStore((s) => s.closeCheckout);
  const watched = useTownStore((s) => s.watchedOrders);
  const onOrder = useOrderEffects();
  return (
    <>
      {request ? <CheckoutDialog key={request.id} request={request} onClose={close} onOrder={onOrder} /> : null}
      {watched.map((id) => (
        <WatchedOrder key={id} orderId={id} onOrder={onOrder} />
      ))}
      <CommerceNotifier />
    </>
  );
}

/** Polls one order whose dialog was closed before it settled; announces the outcome once, then stops. */
function WatchedOrder({ orderId, onOrder }: { orderId: string; onOrder(order: OrderView): void }) {
  const unwatch = useTownStore((s) => s.unwatchOrder);
  const pushToast = useTownStore((s) => s.pushToast);
  const attempt = useRef(0);
  const q = useOrderQuery(orderId, true, (query) => {
    const data = query.state.data as OrderView | undefined;
    if (!data) return 3000;
    const d = orderPollDelayMs(data.status, Math.min(attempt.current++, 5));
    return d === false ? false : Math.max(d, 3000);
  });
  const order = q.data ?? null;
  useEffect(() => {
    if (!order) return;
    onOrder(order);
    if (isSettled(order.status)) {
      if (order.status !== "fulfilled") pushToast(`${order.description || "Your order"}: ${order.message}`, order.status === "manual_review" ? "danger" : "info");
      unwatch(orderId);
    }
  }, [order, orderId, onOrder, unwatch, pushToast]);
  useEffect(() => {
    if (q.error && isApiError(q.error) && (q.error.status === 401 || q.error.status === 404)) unwatch(orderId);
  }, [q.error, orderId, unwatch]);
  return null;
}

const NOTICE_POLL_MS = 60_000;

/**
 * "Your store was taken over: you received $Y Townsy Credit" and "purchase complete" notices for a
 * signed-in resident, from the server's notifications (or the credit journal where the
 * notifications route is not available). Each notice is shown once per browser; the wallet chip
 * refreshes with it.
 */
function CommerceNotifier() {
  const boot = useBootstrapQuery();
  const userId = boot.data?.session.userId ?? null;
  const signedIn = Boolean(userId);
  const navigate = useNavigate();
  const pushToast = useTownStore((s) => s.pushToast);
  const setUi = useTownStore((s) => s.setUi);
  const openThemePicker = useTownStore((s) => s.openThemePicker);
  const openPlacementPicker = useTownStore((s) => s.openPlacementPicker);
  const openStoreSetup = useTownStore((s) => s.openStoreSetup);
  const openOfferSetup = useTownStore((s) => s.openOfferSetup);
  const inTown = useTownSession() !== null;
  // Balances are polled gently while signed in, so a takeover by someone else shows up without a reload.
  const walletsQ = useWalletsQuery(signedIn);
  const credit = walletsQ.data?.credit.availableCents ?? null;
  const qc = useQueryClient();
  useEffect(() => {
    if (!signedIn) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: queryKeys.wallets }), NOTICE_POLL_MS);
    return () => clearInterval(t);
  }, [signedIn, qc]);

  const notifQ = useQuery({
    queryKey: [...queryKeys.notifications, userId ?? "guest"],
    queryFn: fetchNotifications,
    enabled: signedIn,
    staleTime: 30_000,
    refetchInterval: (query) => (query.state.error ? false : NOTICE_POLL_MS),
    retry: (count, err) => !(isApiError(err) && [401, 403, 404].includes(err.status)) && count < 1,
  });
  // No notifications route on this server: the credit journal is the record of displacement credit.
  const fallback = Boolean(notifQ.error && isApiError(notifQ.error) && notifQ.error.status === 404);
  const historyQ = useQuery({
    queryKey: ["commerce-notices", "credit-history", userId ?? "guest"],
    queryFn: () => fetchWalletHistory("CREDIT_USD"),
    enabled: signedIn && fallback,
    staleTime: 0,
    retry: false,
  });

  // A credit increase is the signal to look for a new notice right away.
  const prevCredit = useRef<number | null>(null);
  const refetchNotices = fallback ? historyQ.refetch : notifQ.refetch;
  useEffect(() => {
    if (credit === null) return;
    const before = prevCredit.current;
    prevCredit.current = credit;
    if (before !== null && credit > before) void refetchNotices();
  }, [credit, refetchNotices]);

  const notices = useMemo<Array<{ notice: CommerceNotice; serverId: string | null }> | null>(() => {
    if (fallback) {
      if (!historyQ.data) return null;
      return historyQ.data.items
        .map(noticeFromCreditHistory)
        .filter((n): n is CommerceNotice => n !== null)
        .map((notice) => ({ notice, serverId: null }));
    }
    if (!notifQ.data) return null;
    return notifQ.data.items
      .filter((n) => !n.readAt)
      .map((n) => {
        const notice = noticeFromNotification(n);
        return notice ? { notice, serverId: n.id } : null;
      })
      .filter((x): x is { notice: CommerceNotice; serverId: string } => x !== null);
  }, [fallback, historyQ.data, notifQ.data]);

  useEffect(() => {
    if (!userId || !notices || notices.length === 0) return;
    const seen = new SeenNotices(userId);
    for (const { notice, serverId } of notices) {
      // Already announced by the checkout in this tab: only mark the server copy read.
      if (notice.kind === "fulfilled" && announced.has(notice.orderId) && serverId) void postNotificationRead(serverId).catch(() => undefined);
    }
    const candidates = notices.filter(({ notice }) => !(notice.kind === "fulfilled" && announced.has(notice.orderId)));
    const fresh = new Set(seen.unseen(candidates.map((c) => c.notice)).map((n) => n.key));
    // Already shown in this browser (by the checkout, or on an earlier page): mark the server copy read as well,
    // or the account's next browser or phone announces the same purchase again (M5 e2e, phone check).
    for (const { notice, serverId } of candidates) if (serverId && seen.has(notice.key)) void postNotificationRead(serverId).catch(() => undefined);
    if (fresh.size === 0) return;
    seen.markSeen([...fresh]);
    for (const { notice: n, serverId } of candidates) {
      if (!fresh.has(n.key)) continue;
      if (n.kind === "advertising") {
        invalidateAdvertising(qc, n.refresh);
        const dashboard = { label: "Open advertiser dashboard", run: () => navigate(`/advertise?tab=${n.tab}`) };
        const creativeId = n.showOnStoreCreativeId;
        const storeId = n.storePropertyId;
        const offersStoreId = n.offersPropertyId;
        if (offersStoreId) {
          // ADR-0027: the store's offers (saved on its one screen) changed: that screen, not the dashboard.
          void qc.invalidateQueries({ queryKey: storeOffersKeys.one(offersStoreId) });
          // ADR-0030: "You own <address>. Re-use your offers from <old>?" opens that screen filled in from them.
          pushToast(n.text, n.tone, {
            label: n.reuseOffers ? "Re-use your offers" : n.type === "campaign.approved" || n.type === "campaign.live" || n.type === "campaign.website_renewed" ? "Open your offers" : "Edit offers",
            run: () => openOfferSetup(offersStoreId, "notice", n.reuseOffers ? "reuse" : "offers"),
          });
        } else if (storeId) {
          // ADR-0020: a store-screen design went live or needs a change: the store screen, not the dashboard.
          void qc.invalidateQueries({ queryKey: storeSetupKeys.one(storeId) });
          // ADR-0031: "You own <address>. Re-use your store design (and offers) from <business>?": the store screen opens
          // filled in; after Save the offers screen opens filled in too.
          const reuse = n.storeReuse;
          pushToast(
            n.text,
            n.tone,
            reuse
              ? { label: reuse.offers > 0 ? "Re-use design and offers" : "Re-use your store design", run: () => openStoreSetup(storeId, "notice", { reuse: true }) }
              : { label: n.type === "store.live" ? "Open your store" : "Edit your store", run: () => openStoreSetup(storeId, "notice") },
          );
        } else {
          pushToast(n.text, n.tone, creativeId ? [{ label: "Show on my store", run: () => openPlacementPicker({ creativeId }) }, dashboard] : dashboard);
        }
      } else if (n.kind === "report") {
        void qc.invalidateQueries({ queryKey: ["review", "reports"] });
        pushToast(n.text, "warning", { label: "Open Reports", run: () => navigate("/review?tab=reports") });
      } else if (n.kind === "displaced") {
        void qc.invalidateQueries({ queryKey: queryKeys.wallets });
        void qc.invalidateQueries({ queryKey: ["wallets", "history"] });
        pushToast(n.text, "info", {
          label: "Open wallet",
          run: () => (inTown ? setUi({ walletOpen: true, menuOpen: false }) : navigate("/account")),
        });
      } else {
        announced.add(n.orderId);
        // The notification carries the property, not the order kind: an order on a store is a store purchase.
        const kind = n.propertyId && getPlot(WORLD_MANIFEST, n.propertyId)?.type === "COMMERCIAL" ? "COMMERCIAL_ACQUISITION" : null;
        pushToast(
          n.text,
          "success",
          fulfilledToastActions(
            { orderId: n.orderId, kind, propertyId: n.propertyId },
            { viewReceipt: (id) => navigate(`/orders/${id}`), chooseInterior: openThemePicker, setUpStore: (id) => openStoreSetup(id, "purchase") },
          ),
        );
      }
      if (serverId) void postNotificationRead(serverId).catch(() => undefined);
    }
  }, [notices, userId, qc, pushToast, setUi, navigate, inTown, openThemePicker, openPlacementPicker, openStoreSetup, openOfferSetup]);

  return null;
}
